import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { lawyerService, authService, searchService } from "../../services/api";
import { updateFromProfile } from "../../store/slices/authSlice";
import { ROLES } from "../../config/roles";
import useTranslate from "../../hooks/useTranslate";
import LawyerProfileForm from "../../components/lawyer/LawyerProfileForm";
import LawyerCardPreview from "../../components/lawyer/LawyerCardPreview";
import SettingsSidebar from "../../components/settings/SettingsSidebar";

// "Lawyer profile" settings surface. Visible content is gated by the Lawyer
// role (from the login roles list). Users WITHOUT the role can add it here
// ("I'm also a lawyer" -> POST /create-lawyer/ with the profile name), which
// auto-creates a minimal card; completion happens in this same form. Agent /
// other UI is untouched — this is an additive, role-gated page.
const LawyerProfileSettings = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { user, roles } = useSelector((state) => state.auth);

  const isLawyer = useMemo(
    () => (Array.isArray(roles) ? roles : []).includes(ROLES.Lawyer),
    [roles]
  );
  const displayName =
    user?.name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    "";

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [countrySuggestions, setCountrySuggestions] = useState([]);
  // A3 "Preview my card": the composed card object while the preview is open,
  // null otherwise. Built purely from data this page ALREADY holds (own
  // profile GET + redux auth + the form's live state) — no extra queries.
  const [preview, setPreview] = useState(null);

  const fetchProfile = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    const res = await lawyerService.getMyProfile(signal);
    if (res.success) {
      setProfile(res.data || {});
    } else if (res.status === 404) {
      // No lawyer profile yet — treat as an empty, completable profile.
      setProfile({});
    } else {
      setError(res.error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    if (isLawyer) {
      fetchProfile(controller.signal);
    } else {
      setLoading(false);
    }
    return () => controller.abort();
  }, [isLawyer, fetchProfile]);

  // Country suggestions for the jurisdictions field (best-effort).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await searchService.getCountries("");
        if (active && res?.success && Array.isArray(res.data)) {
          setCountrySuggestions(res.data.map((c) => c.name).filter(Boolean));
        }
      } catch (e) {
        /* non-blocking */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleSave = async (form) => {
    setSaving(true);
    const res = await lawyerService.updateMyProfile(form);
    if (res.success) {
      setProfile(res.data || form);
      toast.success(t("Profile saved"));
    } else {
      toast.error(res.error || t("Failed to save lawyer profile"));
    }
    setSaving(false);
  };

  // A3 "Preview my card" (FOLLOWUP-PACK-BRIEF / FUNCTION-TODO row 8): map the
  // lawyer's OWN data onto the exact shape the directory's LawyerCard consumes.
  // Card fields come from the form's LIVE state (what the preview is for);
  // identity/verification come from the loaded profile with redux-auth
  // fallbacks. The contact variant follows the real email opt-in toggle:
  // ON → chat+email card, OFF → chat-only card (the same rule the directory
  // serializer applies). Read-only — nothing is queried or saved here.
  const handlePreview = (form) => {
    setPreview({
      account_id: user?.account_id ?? user?.id ?? null,
      name: profile?.name || displayName,
      profile_picture_url: profile?.profile_picture_url || null,
      firm: form.firm,
      city: form.city,
      country: form.country,
      specializations: form.specializations,
      jurisdictions: form.jurisdictions,
      languages: form.languages,
      is_verified: profile?.is_verified === true,
      is_dual: (Array.isArray(roles) ? roles : []).includes(ROLES.Agent),
      email: form.contact_email_public
        ? profile?.email || user?.email || ""
        : "",
    });
  };

  const handleAddRole = async () => {
    if (!displayName) {
      toast.error(t("Add your name to your profile first"));
      return;
    }
    setAddingRole(true);
    const res = await authService.createLawyer({ name: displayName });
    if (res.success) {
      // Reflect the new role locally so this session sees the lawyer surfaces.
      const nextRoles = Array.from(new Set([...(roles || []), ROLES.Lawyer]));
      dispatch(updateFromProfile({ roles: nextRoles }));
      toast.success(t("Lawyer role added"));
      setProfile({});
    } else {
      toast.error(res.error || t("Failed to add lawyer role"));
    }
    setAddingRole(false);
  };

  // Artboards 1i/1j: page on #F5F7FB, main column padding 26/32/32. The
  // settings SIDEBAR ships in the A4 follow-up scope (FOLLOWUP-PACK-BRIEF,
  // C23): it renders ONLY the entries with real routes (Lawyer profile /
  // Notifications / Security) — the artboards' Account / Agency profile /
  // Billing rows have no pages or routes and are NOT rendered or built.
  // The lawyer-state page header (title + subtitle + status pill) lives in
  // LawyerProfileForm, which owns the completion state that drives it.
  return (
    <div className="min-h-screen bg-[#F5F7FB] md:flex md:items-stretch">
      <SettingsSidebar active="lawyer-profile" />
      <div className="flex-1 min-w-0">
      <div className="max-w-[884px] mx-auto px-4 md:px-8 pt-[26px] pb-8">
        {!isLawyer ? (
          <>
            <h1 className="text-[19px] font-bold text-[#101828]">
              {t("Lawyer profile")}
            </h1>
            <p className="text-[13px] text-[#667085] mt-[3px] mb-[18px]">
              {t("Manage how you appear in the lawyers directory")}
            </p>
            <div className="bg-white rounded-[12px] border border-[#E6EAF2] px-[18px] py-[16px] text-center">
              <div className="py-8">
                <h2 className="text-[16px] font-semibold text-[#101828]">
                  {t("Are you also a lawyer?")}
                </h2>
                <p className="text-[13px] text-[#667085] mt-1 max-w-md mx-auto">
                  {t(
                    "Add the lawyer role to appear in the verified lawyers directory. Your existing account keeps all its current functionality."
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleAddRole}
                  disabled={addingRole}
                  className="mt-5 bg-[#2283FF] hover:bg-[#1570E8] text-white rounded-[9px] px-[18px] py-[9px] text-[13px] font-semibold disabled:opacity-60"
                >
                  {addingRole ? t("Adding...") : t("I'm also a lawyer")}
                </button>
              </div>
            </div>
          </>
        ) : loading ? (
          <>
            <h1 className="text-[19px] font-bold text-[#101828]">
              {t("Lawyer profile")}
            </h1>
            <p className="text-[13px] text-[#667085] mt-[3px] mb-[18px]">
              {t("Manage how you appear in the lawyers directory")}
            </p>
            <div className="py-16 flex justify-center">
              <div className="h-6 w-6 rounded-full border-2 border-[#DDE3EC] border-t-[#2283FF] animate-spin" />
            </div>
          </>
        ) : error ? (
          <>
            <h1 className="text-[19px] font-bold text-[#101828]">
              {t("Lawyer profile")}
            </h1>
            <p className="text-[13px] text-[#667085] mt-[3px] mb-[18px]">
              {t("Manage how you appear in the lawyers directory")}
            </p>
            <div className="bg-white rounded-[12px] border border-[#E6EAF2] text-center py-12 px-4">
              <p className="text-[#B42318] text-sm">{error}</p>
              <button
                onClick={() => fetchProfile()}
                className="mt-4 bg-[#2283FF] hover:bg-[#1570E8] text-white rounded-[9px] px-[18px] py-[9px] text-[13px] font-semibold"
              >
                {t("Try Again")}
              </button>
            </div>
          </>
        ) : (
          <LawyerProfileForm
            initialProfile={profile}
            onSave={handleSave}
            saving={saving}
            countrySuggestions={countrySuggestions}
            contactEmail={user?.email || ""}
            onPreview={handlePreview}
            t={t}
          />
        )}
      </div>
      </div>
      {preview && (
        <LawyerCardPreview
          lawyer={preview}
          onClose={() => setPreview(null)}
          t={t}
        />
      )}
    </div>
  );
};

export default LawyerProfileSettings;
